import React, { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Eye,
  EyeOff,
  Search,
} from "lucide-react";

// Soglie configurabili per il flag anomalia.
const ANOMALY_OLLI_REQUESTS = 100;
const ANOMALY_OLLI_COST_USD = 5;
const SHOW_EMAILS_KEY = "adminShowEmails";
const TRIAL_DAYS = 30;

const fmtInt = (n) => new Intl.NumberFormat("it-IT").format(n || 0);
const fmtUSD = (n) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(n || 0);

function fmtDate(ms) {
  if (!ms) return "—";
  const d = new Date(ms);
  return d.toLocaleDateString("it-IT");
}

function maskEmail(email) {
  if (!email) return "—";
  const [local, domain] = email.split("@");
  if (!local || !domain) return email;
  const head = local.length > 0 ? local[0] : "*";
  return `${head}***@${domain}`;
}

function PlanBadge({ role, darkMode }) {
  const map = {
    admin: {
      label: "Admin",
      classes: darkMode
        ? "bg-red-900/30 text-red-300"
        : "bg-red-100 text-red-700",
    },
    trial: {
      label: "Trial",
      classes: darkMode
        ? "bg-forfy-500/20 text-forfy-200"
        : "bg-forfy-100 text-forfy-800",
    },
    user: {
      label: "Free",
      classes: darkMode
        ? "bg-neutral-800 text-neutral-300"
        : "bg-neutral-100 text-neutral-700",
    },
  };
  const v = map[role] || map.user;
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${v.classes}`}
    >
      {v.label}
    </span>
  );
}

function trialDaysLeft(role, createdAtMs) {
  if (role !== "trial" || !createdAtMs) return null;
  const elapsed = (Date.now() - createdAtMs) / (24 * 60 * 60 * 1000);
  return Math.max(0, Math.ceil(TRIAL_DAYS - elapsed));
}

function SortHeader({ label, field, sortBy, sortDir, onSort, align, darkMode }) {
  const active = sortBy === field;
  const Icon = !active ? ArrowUpDown : sortDir === "asc" ? ArrowUp : ArrowDown;
  const alignClass =
    align === "right"
      ? "justify-end"
      : align === "center"
        ? "justify-center"
        : "justify-start";
  return (
    <th
      className={`px-3 py-2 text-xs font-semibold uppercase tracking-wide ${
        darkMode ? "text-neutral-400" : "text-neutral-500"
      }`}
    >
      <button
        type="button"
        onClick={() => onSort(field)}
        className={`w-full flex items-center gap-1 ${alignClass} hover:text-forfy-600 transition-colors`}
      >
        {label}
        <Icon className="w-3 h-3 opacity-60" />
      </button>
    </th>
  );
}

function PlainHeader({ label, align, darkMode }) {
  const alignClass =
    align === "right"
      ? "text-right"
      : align === "center"
        ? "text-center"
        : "text-left";
  return (
    <th
      className={`px-3 py-2 text-xs font-semibold uppercase tracking-wide ${alignClass} ${
        darkMode ? "text-neutral-400" : "text-neutral-500"
      }`}
    >
      {label}
    </th>
  );
}

function SkeletonRow({ darkMode }) {
  const cell = darkMode ? "bg-neutral-800" : "bg-neutral-200";
  return (
    <tr>
      {Array.from({ length: 11 }).map((_, i) => (
        <td key={i} className="px-3 py-3">
          <div className={`h-3 rounded ${cell} animate-pulse`} />
        </td>
      ))}
    </tr>
  );
}

export default function UsersTable({ data, loading, darkMode }) {
  const [showEmails, setShowEmails] = useState(() => {
    try {
      return localStorage.getItem(SHOW_EMAILS_KEY) === "true";
    } catch {
      return false;
    }
  });
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("lastLoginAt");
  const [sortDir, setSortDir] = useState("desc");

  useEffect(() => {
    try {
      localStorage.setItem(SHOW_EMAILS_KEY, String(showEmails));
    } catch {
      /* localStorage non disponibile */
    }
  }, [showEmails]);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortDir(field === "createdAt" || field === "lastLoginAt" ? "desc" : "desc");
    }
  };

  const filtered = useMemo(() => {
    const list = data || [];
    if (!search.trim()) return list;
    const q = search.trim().toLowerCase();
    return list.filter((u) => (u.email || "").toLowerCase().includes(q));
  }, [data, search]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      const av = a[sortBy];
      const bv = b[sortBy];
      const aNull = av == null;
      const bNull = bv == null;
      if (aNull && bNull) return 0;
      if (aNull) return 1; // null sempre in fondo
      if (bNull) return -1;
      if (av === bv) return 0;
      const cmp = av > bv ? 1 : -1;
      return sortDir === "asc" ? cmp : -cmp;
    });
    return arr;
  }, [filtered, sortBy, sortDir]);

  const showSkeleton = loading && (!data || data.length === 0);
  const showEmpty = !loading && (!data || data.length === 0);
  const showFilteredEmpty =
    !loading && data && data.length > 0 && sorted.length === 0;

  return (
    <div
      className={`rounded-xl border ${
        darkMode
          ? "bg-neutral-900 border-neutral-800"
          : "bg-white border-neutral-200"
      }`}
    >
      {/* Header sezione */}
      <div
        className={`flex flex-col md:flex-row md:items-center md:justify-between gap-3 p-4 border-b ${
          darkMode ? "border-neutral-800" : "border-neutral-200"
        }`}
      >
        <div>
          <h2
            className={`font-display text-lg font-semibold ${
              darkMode ? "text-neutral-100" : "text-neutral-900"
            }`}
          >
            Utenti
          </h2>
          <p
            className={`text-xs ${
              darkMode ? "text-neutral-400" : "text-neutral-500"
            }`}
          >
            Dettaglio utilizzo per singolo utente
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm ${
              darkMode
                ? "bg-neutral-800 text-neutral-200"
                : "bg-neutral-100 text-neutral-700"
            }`}
          >
            <Search className="w-4 h-4 opacity-60" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cerca per email..."
              className="bg-transparent outline-none text-sm w-44"
            />
          </div>
          <button
            type="button"
            onClick={() => setShowEmails((v) => !v)}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              darkMode
                ? "bg-neutral-800 text-neutral-200 hover:bg-neutral-700"
                : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200"
            }`}
            title={showEmails ? "Nascondi email" : "Mostra email"}
          >
            {showEmails ? (
              <EyeOff className="w-3.5 h-3.5" />
            ) : (
              <Eye className="w-3.5 h-3.5" />
            )}
            {showEmails ? "Nascondi email" : "Mostra email"}
          </button>
        </div>
      </div>

      {/* Tabella */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead
            className={`${
              darkMode ? "bg-neutral-900/50" : "bg-neutral-50"
            } border-b ${
              darkMode ? "border-neutral-800" : "border-neutral-200"
            }`}
          >
            <tr>
              <PlainHeader label="Email" darkMode={darkMode} />
              <SortHeader
                label="Iscritto"
                field="createdAt"
                sortBy={sortBy}
                sortDir={sortDir}
                onSort={handleSort}
                darkMode={darkMode}
              />
              <SortHeader
                label="Ultimo accesso"
                field="lastLoginAt"
                sortBy={sortBy}
                sortDir={sortDir}
                onSort={handleSort}
                darkMode={darkMode}
              />
              <SortHeader
                label="Documenti"
                field="documentsTotal"
                sortBy={sortBy}
                sortDir={sortDir}
                onSort={handleSort}
                align="right"
                darkMode={darkMode}
              />
              <SortHeader
                label="Olli (mese)"
                field="olliRequestsMonth"
                sortBy={sortBy}
                sortDir={sortDir}
                onSort={handleSort}
                align="right"
                darkMode={darkMode}
              />
              <SortHeader
                label="Costo Olli"
                field="olliCostUSDMonth"
                sortBy={sortBy}
                sortDir={sortDir}
                onSort={handleSort}
                align="right"
                darkMode={darkMode}
              />
              <SortHeader
                label="Email mese"
                field="emailMonth"
                sortBy={sortBy}
                sortDir={sortDir}
                onSort={handleSort}
                align="right"
                darkMode={darkMode}
              />
              <SortHeader
                label="Storage"
                field="storageEstimateKB"
                sortBy={sortBy}
                sortDir={sortDir}
                onSort={handleSort}
                align="right"
                darkMode={darkMode}
              />
              <PlainHeader
                label="Piano"
                align="center"
                darkMode={darkMode}
              />
              <PlainHeader
                label="Trial"
                align="center"
                darkMode={darkMode}
              />
              <PlainHeader label="" align="center" darkMode={darkMode} />
            </tr>
          </thead>
          <tbody>
            {showSkeleton &&
              Array.from({ length: 5 }).map((_, i) => (
                <SkeletonRow key={i} darkMode={darkMode} />
              ))}

            {showEmpty && (
              <tr>
                <td
                  colSpan={11}
                  className={`px-3 py-10 text-center text-sm ${
                    darkMode ? "text-neutral-400" : "text-neutral-500"
                  }`}
                >
                  Nessun utente trovato
                </td>
              </tr>
            )}

            {showFilteredEmpty && (
              <tr>
                <td
                  colSpan={11}
                  className={`px-3 py-10 text-center text-sm ${
                    darkMode ? "text-neutral-400" : "text-neutral-500"
                  }`}
                >
                  Nessun risultato per "{search}"
                </td>
              </tr>
            )}

            {!showSkeleton &&
              sorted.map((u) => {
                const anomaly =
                  u.olliRequestsMonth > ANOMALY_OLLI_REQUESTS ||
                  u.olliCostUSDMonth > ANOMALY_OLLI_COST_USD;
                const docTooltip = `Fatture: ${u.docCounts.invoices} · Clienti: ${u.docCounts.clients} · Timesheet: ${u.docCounts.timesheets} · F24: ${u.docCounts.f24} · Contributi: ${u.docCounts.pensionContributions}`;
                const trialLeft = trialDaysLeft(u.role, u.createdAt);

                return (
                  <tr
                    key={u.uid}
                    className={`border-b last:border-0 ${
                      darkMode
                        ? "border-neutral-800 hover:bg-neutral-800/50"
                        : "border-neutral-100 hover:bg-neutral-50"
                    }`}
                  >
                    <td
                      className={`px-3 py-2 font-mono text-xs ${
                        darkMode ? "text-neutral-200" : "text-neutral-800"
                      }`}
                    >
                      {showEmails ? u.email || "—" : maskEmail(u.email)}
                    </td>
                    <td
                      className={`px-3 py-2 ${
                        darkMode ? "text-neutral-300" : "text-neutral-700"
                      }`}
                    >
                      {fmtDate(u.createdAt)}
                    </td>
                    <td
                      className={`px-3 py-2 ${
                        darkMode ? "text-neutral-300" : "text-neutral-700"
                      }`}
                    >
                      {fmtDate(u.lastLoginAt)}
                    </td>
                    <td
                      className={`px-3 py-2 text-right tabular-nums ${
                        darkMode ? "text-neutral-200" : "text-neutral-800"
                      }`}
                      title={docTooltip}
                    >
                      {fmtInt(u.documentsTotal)}
                    </td>
                    <td
                      className={`px-3 py-2 text-right tabular-nums ${
                        darkMode ? "text-neutral-200" : "text-neutral-800"
                      }`}
                    >
                      {fmtInt(u.olliRequestsMonth)}
                    </td>
                    <td
                      className={`px-3 py-2 text-right tabular-nums ${
                        darkMode ? "text-neutral-200" : "text-neutral-800"
                      }`}
                    >
                      {fmtUSD(u.olliCostUSDMonth)}
                    </td>
                    <td
                      className={`px-3 py-2 text-right tabular-nums ${
                        darkMode ? "text-neutral-200" : "text-neutral-800"
                      }`}
                    >
                      {fmtInt(u.emailMonth)}
                    </td>
                    <td
                      className={`px-3 py-2 text-right tabular-nums ${
                        darkMode ? "text-neutral-300" : "text-neutral-700"
                      }`}
                    >
                      {fmtInt(u.storageEstimateKB)} KB
                    </td>
                    <td className="px-3 py-2 text-center">
                      <PlanBadge role={u.role} darkMode={darkMode} />
                    </td>
                    <td
                      className={`px-3 py-2 text-center tabular-nums ${
                        darkMode ? "text-neutral-300" : "text-neutral-700"
                      }`}
                    >
                      {trialLeft != null ? `${trialLeft}gg` : "—"}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {anomaly && (
                        <AlertTriangle
                          className="w-4 h-4 inline-block text-amber-500"
                          aria-label="Uso anomalo"
                          title={`Anomalia: richieste >${ANOMALY_OLLI_REQUESTS} o costo >$${ANOMALY_OLLI_COST_USD}`}
                        />
                      )}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      {/* Privacy notice */}
      <div
        className={`px-4 py-3 border-t text-[11px] ${
          darkMode
            ? "border-neutral-800 text-neutral-500"
            : "border-neutral-200 text-neutral-400"
        }`}
      >
        Dati visualizzati a scopo di monitoraggio del servizio Forfy. Gli
        accessi a questa sezione sono loggati.
      </div>
    </div>
  );
}
